<template>
  <div class="absolute top-0 left-0 z-50 w-screen h-screen">
    <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-85">
      <div class="z-10 w-screen h-full">
        <div class="h-full px-3 py-6 sm:p-10">
          <div
            class="relative grid grid-rows-[auto_1fr_auto] sm:grid-rows-[auto_1fr] w-full h-full overflow-y-auto sm:overflow-hidden bg-white rounded-lg shadow-xl">
            <div class="sm:sticky flex justify-between px-3 py-1.5 bg-cyan-light">
              <h1 class="text-sm font-medium text-theme-500">
                {{ $t('salesDocuments.reportPreview') }}
              </h1>
              <button type="button" @click="emit('close')">
                <Icon :icon="'@documents:ic:reduce'" class="w-4 h-4 ml-2 text-theme-700"></Icon>
              </button>
            </div>

            <div
              v-if="reportTemplate"
              class="grid w-full max-h-[99%] h-full grid-rows-[auto_1fr] px-3 overflow-y-auto">
              <div id="viewer" class="w-full min-h-0"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Icon } from '@iconify/vue';
import { onMounted, ref } from 'vue';
import type { ReportingTemplate } from '@/types/Reporting';
import { getReportingSchema, getReportingTemplateById } from '@/services/api/reporting';
import type { Stimulsoft as StimulsoftNS } from 'stimulsoft-reports-js';
import type { SalesDocument } from '@/types/Documents';

const Stimulsoft = (window as any).Stimulsoft as typeof StimulsoftNS;

type StiViewer = StimulsoftNS.Viewer.StiViewer;

const emit = defineEmits(['close']);

const props = defineProps<{
  saleDocument: SalesDocument;
  defaultReportingTemplateId: string;
}>();

const viewer = ref<StiViewer>();
const reportTemplate = ref<ReportingTemplate | null>(null);

onMounted(async () => {
  const res = await getReportingTemplateById(props.defaultReportingTemplateId);
  reportTemplate.value = res;
  await createViewer();
});

async function createViewer() {
  const options = new Stimulsoft.Viewer.StiViewerOptions();
  options.appearance.fullScreenMode = false;
  options.toolbar.showDesignButton = false;
  options.toolbar.showSaveButton = false;
  options.toolbar.showPrintButton = false;
  options.toolbar.showOpenButton = false;

  const report = new Stimulsoft.Report.StiReport();
  report.load(reportTemplate.value?.template);

  if (reportTemplate.value?.datasourceScript) {
    let runtimeParams: Record<string, unknown> = {};
    const json = reportTemplate.value?.parametersJson;
    if (json) {
      const meta = JSON.parse(json) as Record<string, any>;
      const paramName = Object.keys(meta)[0];
      if (paramName) {
        runtimeParams[paramName] = props.saleDocument.id;
      }
    }
    const schema = await getReportingSchema(reportTemplate.value!.datasourceScript!, runtimeParams);
    const ds = new Stimulsoft.System.Data.DataSet('Schema');
    ds.readJson(schema);
    report.regData('Schema', 'Schema', ds);
    report.dictionary?.synchronize();
  }
  viewer.value = new Stimulsoft.Viewer.StiViewer(options, 'StiViewer', false);
  viewer.value.report = report;
  viewer.value.renderHtml('viewer');
}
</script>
